import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { GridTreeDataGroupingCell } from '@mui/x-data-grid-pro';
import { StatusBadgeGrid } from '@AMIEWEB/Tasks/CustomComponents/StatusBadgeGrid';
import { missingField } from 'app/constants';
import {
  normalizeTaskGridCellDisplay,
  OverflowTooltipCell,
  OverflowTooltipWrapper,
} from '@AMIEWEB/GlobalSearch/Tasks/taskSearchGridTooltipCells';
import {
  formatModsLinkTaskDate,
  formatModsLinkTaskDateOnly,
  numericIdSortComparator,
} from './modsLinkTaskSearchGridUtils';
import { ModsLinkTaskApplyRadioCell } from './ModsLinkTaskSearchGridSelection';

const isBlankId = (v: unknown) => !v || v === missingField || v === '—' || v === '0';

export interface ModsLinkTaskSearchColumnOptions {
  theme: Theme;
  placementBrandId: number | undefined;
}

export function getModsLinkTaskSearchColumnDefs({
  theme,
  placementBrandId,
}: ModsLinkTaskSearchColumnOptions): GridColDef[] {
  return [
    {
      field: 'apply',
      headerName: 'Apply',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: params => <ModsLinkTaskApplyRadioCell gridRowId={params.id} row={params.row} />,
    } as GridColDef,
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: params => {
        const statusId = params.row?.statusId;
        if (statusId == null) {
          return <Box>{missingField}</Box>;
        }
        return <StatusBadgeGrid statusId={statusId} />;
      },
    },
    {
      field: 'from',
      headerName: 'From',
      width: 150,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.row?.from)} />
      ),
    },
    {
      field: 'recipients',
      headerName: 'Recipients',
      width: 200,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.row?.recipients)} />
      ),
    },
    {
      field: 'sentDate',
      headerName: 'Sent Date',
      width: 120,
      valueGetter: params => formatModsLinkTaskDate(params.row?.sentDate),
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.value)} />
      ),
    },
    {
      field: 'isFollowup',
      headerName: 'Follow-Up',
      width: 100,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.row?.isFollowup)} />
      ),
    },
    {
      field: 'followupDate',
      headerName: 'Follow-Up Date',
      width: 140,
      valueGetter: params => formatModsLinkTaskDateOnly(params.row?.followupDate),
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.value)} />
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.row?.priority)} />
      ),
    },
    {
      field: 'message',
      headerName: 'Message',
      width: 250,
      renderCell: params => {
        const messageText = normalizeTaskGridCellDisplay(params.row?.message);
        return <OverflowTooltipWrapper tooltipText={messageText}>{messageText}</OverflowTooltipWrapper>;
      },
    },
    {
      field: 'isOptionApproval',
      headerName: 'Option Approval',
      width: 130,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.row?.isOptionApproval)} />
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 150,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.row?.category)} />
      ),
    },
    {
      field: 'isFlagged',
      headerName: 'Flagged',
      width: 100,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.row?.isFlagged)} />
      ),
    },
    {
      field: 'isCoverage',
      headerName: 'Coverage',
      width: 100,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.row?.isCoverage)} />
      ),
    },
    {
      field: 'coveredFor',
      headerName: 'Covered For',
      width: 150,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.row?.coveredFor)} />
      ),
    },
    {
      field: 'facilityID',
      headerName: 'Facility ID',
      width: 120,
      sortComparator: numericIdSortComparator,
      renderCell: params => {
        const facilityId = params.row?.facilityID;
        if (isBlankId(facilityId)) {
          return <Box>{missingField}</Box>;
        }
        const to = `/facility/${facilityId}`;
        return (
          <RouterLink
            to={to}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
            onClick={e => e.stopPropagation()}
          >
            {facilityId}
          </RouterLink>
        );
      },
    },
    {
      field: 'facility',
      headerName: 'Facility',
      width: 200,
      renderCell: params => {
        const facilityId = params.row?.facilityID;
        const facilityName = params.row?.facility;
        if (isBlankId(facilityId) || facilityName === '—' || facilityName === missingField) {
          return <Box>{missingField}</Box>;
        }
        const to = `/facility/${facilityId}`;
        return (
          <OverflowTooltipWrapper tooltipText={String(facilityName)}>
            <RouterLink
              to={to}
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
              onClick={e => e.stopPropagation()}
            >
              {facilityName}
            </RouterLink>
          </OverflowTooltipWrapper>
        );
      },
    },
    {
      field: 'unitID',
      headerName: 'Unit ID',
      width: 100,
      sortComparator: numericIdSortComparator,
      renderCell: params => {
        const unitId = params.row?.unitID;
        const facilityId = params.row?.facilityID;
        const noLink = isBlankId(unitId) || isBlankId(facilityId);
        if (noLink) {
          return <Box>{missingField}</Box>;
        }
        const to = `/facility/${facilityId}/unit/${unitId}`;
        return (
          <RouterLink
            to={to}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
            onClick={e => e.stopPropagation()}
          >
            {unitId}
          </RouterLink>
        );
      },
    },
    {
      field: 'unit',
      headerName: 'Unit',
      width: 150,
      renderCell: params => {
        const unitId = params.row?.unitID;
        const unitName = params.row?.unit;
        const facilityId = params.row?.facilityID;
        const noLink =
          isBlankId(unitId) ||
          unitName === '—' ||
          unitName === missingField ||
          isBlankId(facilityId);
        if (noLink) {
          return <Box>{missingField}</Box>;
        }
        const to = `/facility/${facilityId}/unit/${unitId}`;
        return (
          <OverflowTooltipWrapper tooltipText={String(unitName)}>
            <RouterLink
              to={to}
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
              onClick={e => e.stopPropagation()}
            >
              {unitName}
            </RouterLink>
          </OverflowTooltipWrapper>
        );
      },
    },
    {
      field: 'orderID',
      headerName: 'Order ID',
      width: 100,
      sortComparator: numericIdSortComparator,
      renderCell: params => {
        const orderId = params.row?.orderID;
        if (isBlankId(orderId)) {
          return <Box>{missingField}</Box>;
        }
        const to = `/order/${orderId}`;
        return (
          <RouterLink
            to={to}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
            onClick={e => e.stopPropagation()}
          >
            {orderId}
          </RouterLink>
        );
      },
    },
    {
      field: 'placementID',
      headerName: 'Placement ID',
      width: 130,
      sortComparator: numericIdSortComparator,
      renderCell: params => {
        const placementId = params.row?.placementID;
        if (isBlankId(placementId)) {
          return <Box>{missingField}</Box>;
        }
        const to = `/placement/${placementId}`;
        return (
          <RouterLink
            to={to}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
            onClick={e => e.stopPropagation()}
          >
            {placementId}
          </RouterLink>
        );
      },
    },
    {
      field: 'candidateID',
      headerName: 'Candidate ID',
      width: 130,
      sortComparator: numericIdSortComparator,
      renderCell: params => {
        const candidateId = params.row?.candidateID;
        const brandId = placementBrandId;
        if (isBlankId(candidateId)) {
          return <Box>{missingField}</Box>;
        }
        if (brandId == null) {
          return <Box>{candidateId}</Box>;
        }
        const to = `/candidate/${candidateId}/${brandId}`;
        return (
          <RouterLink
            to={to}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
            onClick={e => e.stopPropagation()}
          >
            {candidateId}
          </RouterLink>
        );
      },
    },
    {
      field: 'candidate',
      headerName: 'Candidate',
      width: 200,
      renderCell: params => {
        const candidateId = params.row?.candidateID;
        const candidateName = params.row?.candidate;
        const brandId = placementBrandId;
        if (isBlankId(candidateId) || candidateName === '—' || candidateName === missingField) {
          return <Box>{missingField}</Box>;
        }
        if (brandId == null) {
          return <OverflowTooltipCell value={normalizeTaskGridCellDisplay(candidateName)} />;
        }
        const to = `/candidate/${candidateId}/${brandId}`;
        return (
          <OverflowTooltipWrapper tooltipText={String(candidateName)}>
            <RouterLink
              to={to}
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
              onClick={e => e.stopPropagation()}
            >
              {candidateName}
            </RouterLink>
          </OverflowTooltipWrapper>
        );
      },
    },
    {
      field: 'contractAffiliateID',
      headerName: 'Contract Affiliate ID',
      width: 130,
      sortComparator: numericIdSortComparator,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.value)} />
      ),
    },
    {
      field: 'affiliateContactID',
      headerName: 'Affiliate Contact ID',
      width: 130,
      sortComparator: numericIdSortComparator,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.value)} />
      ),
    },
    {
      field: 'facilityContactID',
      headerName: 'Facility Contact ID',
      width: 130,
      sortComparator: numericIdSortComparator,
      renderCell: params => (
        <OverflowTooltipCell value={normalizeTaskGridCellDisplay(params.value)} />
      ),
    },
  ];
}

export function getModsLinkTaskSearchGroupingColDef(theme: Theme): GridColDef {
  return {
    headerName: 'Subject',
    width: 300,
    sortable: true,
    sortComparator: (_v1, _v2, cellParams1, cellParams2) => {
      const p1 = cellParams1 as any;
      const p2 = cellParams2 as any;
      const row1 = p1.row ?? p1.api?.getRow?.(p1.id);
      const row2 = p2.row ?? p2.api?.getRow?.(p2.id);
      const a = (row1?.subject ?? '').toString();
      const b = (row2?.subject ?? '').toString();
      return a.localeCompare(b);
    },
    renderCell: params => {
      const subjectText = normalizeTaskGridCellDisplay(params.row?.subject);
      return (
        <OverflowTooltipWrapper
          tooltipText={subjectText}
          sx={{
            overflow: 'hidden',
            '& .MuiDataGrid-treeDataGroupingCell > span': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        >
          <GridTreeDataGroupingCell {...params} formattedValue={subjectText} />
        </OverflowTooltipWrapper>
      );
    },
  };
}
